import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import ProductVisual from "./ProductVisual";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (n) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

function PromoField() {
  const { promo, setPromo } = useCart();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const apply = async () => {
    if (!code.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/promos/validate/${encodeURIComponent(code.trim())}`);
      setPromo(data);
    } catch {
      setError("Ce code n'est pas valide.");
    } finally {
      setLoading(false);
    }
  };

  if (promo) {
    return (
      <div data-testid="promo-applied" className="flex items-center justify-between text-sm border border-gold/50 px-3 py-2 mb-3">
        <span className="text-gold">✦ {promo.code} — {promo.description}</span>
        <button data-testid="promo-remove" onClick={() => setPromo(null)} aria-label="Retirer le code" className="text-ivory/80 hover:text-gold transition-colors">
          <X size={14} />
        </button>
      </div>
    );
  }
  return (
    <div className="mb-3">
      <div className="flex gap-2">
        <input
          data-testid="promo-input"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === "Enter" && apply()}
          placeholder="Code promo"
          className="flex-1 bg-transparent border border-ivory/25 focus:border-gold outline-none px-3 py-2 text-sm placeholder:text-ivory/40 transition-colors"
        />
        <button
          data-testid="promo-apply-button"
          onClick={apply}
          disabled={loading}
          className="border border-ivory/25 px-4 text-xs uppercase tracking-[0.15em] hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
        >
          {loading ? "…" : "Appliquer"}
        </button>
      </div>
      {error && <p data-testid="promo-error" className="text-xs text-gold mt-2">{error}</p>}
    </div>
  );
}

export default function CartDrawer({ onCheckout }) {
  const { items, setQty, remove, total, discount, grandTotal, promo, open, setOpen } = useCart();
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-choco-deep/50 z-50"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 w-full sm:w-[420px] bg-denim text-ivory z-50 flex flex-col"
          >
            <div className="flex items-center justify-between px-8 h-20 border-b border-ivory/15">
              <h2 className="font-serif text-2xl font-light">Votre panier</h2>
              <button data-testid="cart-close-button" onClick={() => setOpen(false)} aria-label="Fermer le panier" className="hover:text-gold transition-colors">
                <X size={22} strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-8">
              {items.length === 0 && (
                <p data-testid="cart-empty" className="text-ivory/70 font-light mt-10 text-center font-accent italic text-xl">
                  Votre panier attend son premier trésor…
                </p>
              )}
              {items.map((item) => (
                <div key={item.key} data-testid={`cart-item-${item.key}`} className="flex gap-5">
                  <div className="w-20 h-20 bg-choco shrink-0 flex items-center justify-center overflow-hidden">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <ProductVisual variant={item.visual} className="w-4/5" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between gap-3">
                      <div>
                        <p className="font-serif text-lg font-light">{item.name}</p>
                        <p className="text-xs text-ivory/80">{item.subtitle}{item.size ? ` — Taille ${item.size}` : ""}{item.color ? ` — ${item.color}` : ""}</p>
                      </div>
                      <button data-testid={`cart-remove-${item.key}`} onClick={() => remove(item.key)} aria-label="Retirer" className="text-ivory/50 hover:text-gold transition-colors">
                        <X size={16} />
                      </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-3 border border-ivory/25 px-2 py-1">
                        <button data-testid={`cart-minus-${item.key}`} onClick={() => setQty(item.key, item.qty - 1)} aria-label="Moins"><Minus size={14} /></button>
                        <span className="text-sm w-5 text-center" data-testid={`cart-qty-${item.key}`}>{item.qty}</span>
                        <button data-testid={`cart-plus-${item.key}`} onClick={() => setQty(item.key, item.qty + 1)} aria-label="Plus"><Plus size={14} /></button>
                      </div>
                      <p className="font-serif">{eur(item.price * item.qty)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {items.length > 0 && (
              <div className="px-8 py-5 border-t border-ivory/15">
                <PromoField />
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-ivory/70">Sous-total</span>
                  <span data-testid="cart-subtotal">{eur(total)}</span>
                </div>
                {promo && (
                  <div className="flex justify-between text-sm mb-1" data-testid="cart-discount-row">
                    <span className="text-gold">Remise {promo.code} (-{promo.percent} %)</span>
                    <span className="text-gold" data-testid="cart-discount">-{eur(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-ivory/70">Livraison</span>
                  <span className="text-gold">Offerte</span>
                </div>
                <div className="flex justify-between font-serif text-xl mb-4">
                  <span>Total</span>
                  <span data-testid="cart-total">{eur(grandTotal)}</span>
                </div>
                <button
                  data-testid="checkout-open-button"
                  onClick={onCheckout}
                  className="w-full bg-ivory text-denim text-xs uppercase tracking-[0.25em] py-4 hover:bg-gold hover:text-ivory transition-colors duration-400"
                >
                  Passer commande
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
